// src/components/Clients.js
import React, { useEffect, useMemo, useState } from "react";
import api from "../utils/api";
import { useNavigate } from "react-router-dom";
import {
  FaUserPlus,
  FaEye,
  FaTrash,
  FaSearch,
  FaTimes,
  FaUsers,
  FaIdCard,
  FaPhoneAlt,
  FaBuilding,
  FaCrown,
  FaRegUser,
} from "react-icons/fa";
import toast, { Toaster } from "react-hot-toast";

function Clients() {
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);

  // Search
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);

  // Modal Add
  const [openAdd, setOpenAdd] = useState(false);
  const [adding, setAdding] = useState(false);

  const initialForm = {
    name: "",
    email: "", // ✅ رقم التوكيل
    phone: "",
    type: "عادي",
    officeName: "",
  };

  const [form, setForm] = useState(initialForm);

  /* =======================
      Helpers
  ======================= */
  const normalize = (v) => (typeof v === "string" ? v.trim() : "");

  const isFormValid = useMemo(() => {
    // ✅ مطلوب: الاسم + رقم التوكيل + الهاتف
    return Boolean(
      normalize(form.name) && normalize(form.email) && normalize(form.phone)
    );
  }, [form]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const res = await api.get("/clients");
      setClients(res.data || []);
    } catch (err) {
      console.error("❌ فشل تحميل العملاء:", err);
      toast.error("❌ فشل تحميل العملاء");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const handleSearch = async (q) => {
    setSearch(q);

    const value = normalize(q);
    if (!value) return fetchClients();

    try {
      setSearching(true);
      const res = await api.get(`/clients/search?q=${encodeURIComponent(value)}`);
      setClients(res.data || []);
    } catch (err) {
      console.error("❌ فشل البحث:", err);
      toast.error("❌ فشل البحث");
    } finally {
      setSearching(false);
    }
  };

  const addClient = async () => {
    if (!isFormValid) return toast.error("⚠️ أدخل الاسم + رقم التوكيل + الهاتف");

    try {
      setAdding(true);

      await api.post("/clients", {
        name: normalize(form.name),
        email: normalize(form.email), // ✅ رقم التوكيل
        phone: normalize(form.phone),
        type: normalize(form.type) || "عادي",
        officeName: normalize(form.officeName),
      });

      toast.success("✅ تم إضافة العميل بنجاح");
      setForm(initialForm);
      setOpenAdd(false);
      fetchClients();
    } catch (err) {
      console.error("❌ فشل إضافة عميل:", err.response?.data || err.message);
      toast.error(err.response?.data?.message || "❌ فشل إضافة العميل");
    } finally {
      setAdding(false);
    }
  };

  const deleteClient = async (id) => {
    if (!window.confirm("⚠️ هل تريد حذف هذا العميل؟")) return;

    try {
      await api.delete(`/clients/${id}`);
      toast.success("🗑️ تم حذف العميل");
      fetchClients();
    } catch (err) {
      console.error("❌ فشل حذف العميل:", err);
      toast.error("❌ فشل حذف العميل");
    }
  };

  /* =======================
      UI Components
  ======================= */
  const EmptyState = () => (
    <div className="bg-gray-900 border border-gray-800 rounded-3xl p-6 text-center">
      <p className="text-2xl mb-2">📭</p>
      <p className="text-gray-200 font-extrabold">لا يوجد عملاء حتى الآن</p>
      <p className="text-gray-400 text-sm mt-2">
        أضف أول عميل وسيظهر هنا مباشرة.
      </p>

      <button
        onClick={() => setOpenAdd(true)}
        className="mt-5 w-full bg-yellow-500 hover:bg-yellow-600 text-black font-extrabold px-5 py-3 rounded-2xl"
      >
        <span className="inline-flex items-center justify-center gap-2">
          <FaUserPlus /> إضافة عميل
        </span>
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-950 to-black text-white px-4 pb-24">
      <Toaster position="top-center" />

      {/* Header */}
      <div className="pt-6 pb-5">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-2xl sm:text-3xl font-black text-yellow-400 drop-shadow">
                👥 إدارة العملاء
              </h2>
              <p className="text-gray-400 text-sm mt-1">
                بيانات العميل + رقم التوكيل + البحث الفوري
              </p>
            </div>

            <button
              onClick={() => setOpenAdd(true)}
              className="w-full sm:w-auto bg-yellow-500 hover:bg-yellow-600 text-black font-extrabold px-5 py-3 rounded-2xl shadow-lg"
            >
              <span className="inline-flex items-center justify-center gap-2">
                <FaUserPlus /> إضافة عميل جديد
              </span>
            </button>
          </div>

          {/* Search */}
          <div className="mt-5 bg-gray-900 border border-gray-800 rounded-3xl p-3 sm:p-4 flex items-center gap-3 shadow-xl">
            <div className="w-10 h-10 rounded-2xl bg-yellow-500/15 flex items-center justify-center border border-yellow-500/30">
              <FaSearch className="text-yellow-400" />
            </div>

            <input
              type="text"
              placeholder="ابحث بالاسم / رقم التوكيل / الهاتف..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="flex-1 bg-transparent outline-none text-white placeholder-gray-500 text-base"
            />

            {search ? (
              <button
                onClick={() => {
                  setSearch("");
                  fetchClients();
                }}
                className="text-gray-300 hover:text-white px-3 py-2 rounded-xl bg-gray-800 border border-gray-700"
              >
                مسح
              </button>
            ) : null}
          </div>

          <p className="text-center text-gray-500 text-xs sm:text-sm mt-2">
            {searching ? "⏳ جاري البحث..." : "البحث فوري — اكتب أي جزء من البيانات"}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto">
        {loading ? (
          <div className="text-center text-yellow-400 font-bold mt-20 animate-pulse">
            ⏳ جاري تحميل العملاء...
          </div>
        ) : clients.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* Small stats */}
            <div className="mb-6 flex flex-col sm:flex-row gap-3">
              <div className="bg-gray-900 border border-gray-800 rounded-3xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-yellow-500/15 flex items-center justify-center border border-yellow-500/30">
                  <FaUsers className="text-yellow-400" />
                </div>
                <div>
                  <p className="text-gray-400 text-xs">إجمالي العملاء</p>
                  <p className="font-black text-lg">{clients.length}</p>
                </div>
              </div>

              <div className="bg-gray-900 border border-gray-800 rounded-3xl p-4 flex items-center gap-3 flex-1">
                <div className="w-10 h-10 rounded-2xl bg-green-500/15 flex items-center justify-center border border-green-500/30">
                  <FaIdCard className="text-green-400" />
                </div>
                <div>
                  <p className="text-gray-400 text-xs">ملاحظة</p>
                  <p className="text-gray-200 text-sm font-bold">
                  </p>
                </div>
              </div>
            </div>

            {/* Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {clients.map((c) => {
                const cid = c._id || c.id;
                const isVIP = c.type === "مميز";

                return (
                  <div
                    key={cid}
                    className="bg-gray-900 border border-gray-800 rounded-3xl p-5 shadow-xl hover:border-yellow-500/40 transition"
                  >
                    {/* Name + badge */}
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="text-xl font-black text-yellow-400 break-words">
                        {c.name || "—"}
                      </h3>

                      <span
                        className={`shrink-0 text-xs px-3 py-1 rounded-full border font-bold inline-flex items-center gap-2 ${
                          isVIP
                            ? "border-yellow-500 text-yellow-300 bg-yellow-500/10"
                            : "border-gray-600 text-gray-300 bg-gray-800/40"
                        }`}
                      >
                        {isVIP ? (
                          <>
                            <FaCrown /> مميز
                          </>
                        ) : (
                          <>
                            <FaRegUser /> عادي
                          </>
                        )}
                      </span>
                    </div>

                    {/* Details */}
                    <div className="mt-4 space-y-2 text-sm">
                      <p className="text-gray-200 flex items-center gap-2 break-words">
                        <FaIdCard className="text-yellow-400" />
                        <span className="text-gray-400">رقم التوكيل:</span>
                        <span className="font-bold">{c.email || "—"}</span>
                      </p>

                      <p className="text-gray-200 flex items-center gap-2 break-words">
                        <FaPhoneAlt className="text-yellow-400" />
                        <span className="text-gray-400">الهاتف:</span>
                        <span className="font-bold">{c.phone || "—"}</span>
                      </p>

                      <p className="text-gray-200 flex items-center gap-2 break-words">
                        <FaBuilding className="text-yellow-400" />
                        <span className="text-gray-400">المكتب:</span>
                        <span className="font-bold">{c.officeName || "غير محدد"}</span>
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="mt-5 grid grid-cols-2 gap-2">
                      <button
                        onClick={() => navigate(`/clients/${cid}`)}
                        className="bg-yellow-500 hover:bg-yellow-600 text-black font-extrabold py-3 rounded-2xl"
                      >
                        <span className="inline-flex items-center justify-center gap-2">
                          <FaEye /> التفاصيل
                        </span>
                      </button>

                      <button
                        onClick={() => deleteClient(cid)}
                        className="bg-red-600 hover:bg-red-700 text-white font-extrabold py-3 rounded-2xl"
                      >
                        <span className="inline-flex items-center justify-center gap-2">
                          <FaTrash /> حذف
                        </span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* ✅ Add Client Modal */}
      {openAdd && (
        <div className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center p-3 z-50">
          <div className="w-full max-w-2xl bg-gray-950 border border-gray-800 rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl">
            {/* Modal header */}
            <div className="p-5 border-b border-gray-800 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-black text-yellow-400">
                  ➕ إضافة عميل جديد
                </h3>
                <p className="text-gray-400 text-sm mt-1">
                  أدخل بيانات العميل ثم اضغط حفظ
                </p>
              </div>

              <button
                onClick={() => setOpenAdd(false)}
                className="bg-red-600 hover:bg-red-700 p-2 rounded-2xl"
                title="إغلاق"
              >
                <FaTimes />
              </button>
            </div>

            {/* Modal body */}
            <div className="p-5 max-h-[75vh] overflow-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  className="p-4 rounded-2xl bg-gray-900 text-white border border-gray-800 outline-none focus:border-yellow-500"
                  placeholder="اسم العميل *"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />

                <input
                  className="p-4 rounded-2xl bg-gray-900 text-white border border-gray-800 outline-none focus:border-yellow-500"
                  placeholder="رقم التوكيل *"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />

                <input
                  className="p-4 rounded-2xl bg-gray-900 text-white border border-gray-800 outline-none focus:border-yellow-500"
                  placeholder="رقم الهاتف *"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />

                <input
                  className="p-4 rounded-2xl bg-gray-900 text-white border border-gray-800 outline-none focus:border-yellow-500"
                  placeholder="اسم المكتب (اختياري)"
                  value={form.officeName}
                  onChange={(e) => setForm({ ...form, officeName: e.target.value })}
                />

                <select
                  className="p-4 rounded-2xl bg-gray-900 text-white border border-gray-800 outline-none focus:border-yellow-500 sm:col-span-2"
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                >
                  <option value="عادي">عادي</option>
                  <option value="مميز">مميز</option>
                  <option value="مكتب">مكتب</option>
                  <option value="شركة">شركة</option>
                  <option value="مؤسسة">مؤسسة</option>
                  <option value="جهة حكومية">جهة حكومية</option>
                </select>

                <button
                  onClick={addClient}
                  disabled={adding}
                  className="sm:col-span-2 bg-yellow-500 hover:bg-yellow-600 text-black font-black px-5 py-4 rounded-2xl disabled:opacity-70"
                >
                  {adding ? "⏳ جاري الحفظ..." : "✅ حفظ العميل"}
                </button>

                <button
                  onClick={() => {
                    setForm(initialForm);
                    setOpenAdd(false);
                  }}
                  className="sm:col-span-2 bg-gray-800 hover:bg-gray-700 text-white font-black px-5 py-4 rounded-2xl border border-gray-700"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Clients;
